import { useState, useEffect, useRef } from "react";
import { initTracking, trackPageView, trackCtaFinal } from "./tracking";

/* ── Shared helpers ── */
function sleep(ms) { return new Promise(r => setTimeout(r, ms)); }

function SkeletonLine({ width = "100%", height = 8, mb = 0 }) {
  return (
    <div style={{
      width, height, borderRadius: 5, marginBottom: mb,
      background: "linear-gradient(90deg,#e8eaed 25%,#f3f4f6 50%,#e8eaed 75%)",
      backgroundSize: "200% 100%",
      animation: "shimmerMF 1.8s infinite",
    }}/>
  );
}

/* ── Google Shopping card ── */
function ProductCard({ isOur, title, price, optimized, ranked, scanX, scanning, dim }) {
  return (
    <div className="mf-product-card-f" style={{
      background: "#fff", borderRadius: 16,
      border: isOur && optimized ? "2px solid #4B6AE8" : "1.5px solid #e8eaed",
      boxShadow: isOur && optimized ? "0 8px 32px rgba(75,106,232,0.22)" : "0 1px 4px rgba(0,0,0,0.06)",
      overflow: "hidden", position: "relative", flex: "1 1 0", minWidth: 0, height: 240,
      opacity: dim ? 0.38 : 1, transform: "scale(1)",
      transition: "opacity 0.5s ease, transform 0.6s cubic-bezier(0.34,1.2,0.64,1), border 0.4s ease, box-shadow 0.4s ease",
      zIndex: isOur ? 2 : 1,
    }}>
      {scanning && isOur && (
        <>
          <div style={{ position: "absolute", inset: 0, zIndex: 9, pointerEvents: "none", background: `linear-gradient(90deg, rgba(75,106,232,0.07) ${scanX - 20}%, rgba(75,106,232,0.0) ${scanX + 20}%)` }}/>
          <div style={{ position: "absolute", top: 0, bottom: 0, left: `${scanX}%`, width: 3, zIndex: 10, pointerEvents: "none", background: "linear-gradient(180deg, transparent 0%, #818cf8 20%, #4B6AE8 50%, #818cf8 80%, transparent 100%)", boxShadow: "0 0 18px 7px rgba(75,106,232,0.5)" }}/>
        </>
      )}
      <div className="mf-product-img-f" style={{ height: 90, background: isOur ? (optimized ? "linear-gradient(135deg,#eef2ff,#dde5ff)" : "#f5f5f5") : "#f5f5f5", display: "flex", alignItems: "center", justifyContent: "center", transition: "background 0.6s ease", position: "relative" }}>
        {isOur ? (
          <img src="/backpack.png" alt="Laptop backpack" style={{ width: "80%", height: "85%", objectFit: "contain" }}/>
        ) : (
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none"><rect x="3" y="3" width="18" height="18" rx="3" stroke="#d1d5db" strokeWidth="1.5"/><path d="M3 16l5-5 4 4 3-3 6 6" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><circle cx="8.5" cy="8.5" r="1.5" fill="#d1d5db"/></svg>
        )}
        {isOur && ranked && (
          <div style={{ position: "absolute", top: 8, left: 8, background: "linear-gradient(135deg,#4B6AE8,#818cf8)", color: "#fff", fontSize: 11, fontWeight: 800, padding: "3px 9px", borderRadius: 20, boxShadow: "0 2px 8px rgba(75,106,232,0.45)", animation: "popIn 0.4s cubic-bezier(0.34,1.56,0.64,1)" }}>#1</div>
        )}
      </div>
      <div style={{ padding: "8px 10px 10px" }}>
        <div className="mf-ai-badge-f" style={{ height: 22, marginBottom: 6, opacity: isOur && optimized ? 1 : 0, transform: isOur && optimized ? "translateY(0)" : "translateY(4px)", transition: "all 0.4s ease 0.2s" }}>
          <span style={{ fontSize: 8, fontWeight: 600, color: "#4B6AE8", background: "rgba(75,106,232,0.08)", border: "1px solid rgba(75,106,232,0.18)", padding: "2px 6px", borderRadius: 20, whiteSpace: "nowrap" }}>✦ AI Optimized</span>
        </div>
        <div style={{ minHeight: 36, marginBottom: 4 }}>
          {isOur ? (
            <div style={{ fontSize: 12, fontWeight: 600, color: "#111", lineHeight: 1.4 }}>{title}</div>
          ) : (
            <>
              <div style={{ fontSize: 12, color: "#9aa0a6", marginBottom: 5 }}>Product</div>
              <SkeletonLine width="85%" height={7} mb={4}/><SkeletonLine width="60%" height={7}/>
            </>
          )}
        </div>
        {isOur ? (
          <div style={{ fontSize: 13, fontWeight: 700, color: "#111", marginBottom: 8 }}>{price}</div>
        ) : (
          <div style={{ marginBottom: 8 }}><SkeletonLine width="45%" height={8}/></div>
        )}
        <SkeletonLine width="90%" height={6} mb={4}/><SkeletonLine width="70%" height={6}/>
      </div>
    </div>
  );
}

/* ── Shopping Widget ── */
function ShoppingWidget() {
  const [phase, setPhase] = useState("before");
  const [scanX, setScanX] = useState(-10);
  const [title, setTitle] = useState("Laptop backpack");
  const ivRef = useRef(null);
  const running = useRef(true);
  const BEFORE_TITLE = "Laptop backpack";
  const AFTER_TITLE = 'Premium & Durable Luxury Laptop Backpack — 15.6"';
  const isOptimized = phase === "rewriting" || phase === "ranked";
  const isScanning = phase === "scanning";
  const isRanked = phase === "ranked";

  function animateScan(setSX, duration) {
    return new Promise(resolve => {
      const start = Date.now();
      const tick = () => {
        const elapsed = Date.now() - start;
        const p = Math.min(elapsed / duration, 1);
        const eased = p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2;
        setSX(eased * 110 - 5);
        if (p < 1) requestAnimationFrame(tick); else resolve();
      };
      requestAnimationFrame(tick);
    });
  }

  useEffect(() => {
    running.current = true;
    const run = async () => {
      while (running.current) {
        setPhase("before"); setTitle(BEFORE_TITLE); setScanX(-10);
        await sleep(2500); if (!running.current) break;
        setPhase("scanning");
        await animateScan(setScanX, 1400); if (!running.current) break;
        setPhase("rewriting"); clearInterval(ivRef.current); setTitle("");
        let i = 0;
        await new Promise(resolve => {
          ivRef.current = setInterval(() => { i++; setTitle(AFTER_TITLE.slice(0, i)); if (i >= AFTER_TITLE.length) { clearInterval(ivRef.current); resolve(); } }, 22);
        });
        if (!running.current) break; await sleep(600);
        setPhase("ranked"); await sleep(4200); if (!running.current) break;
      }
    };
    run();
    return () => { running.current = false; clearInterval(ivRef.current); };
  }, []);

  return (
    <div style={{ background: "#fff", borderRadius: 14, border: "1px solid rgba(75,106,232,0.1)", boxShadow: "0 4px 20px rgba(75,106,232,0.07)", overflow: "hidden" }}>
      <div style={{ padding: "10px 12px 0" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
          <svg width="16" height="16" viewBox="0 0 48 48"><path fill="#4285F4" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#34A853" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59A14.5 14.5 0 019.5 24c0-1.59.28-3.14.77-4.59l-7.98-6.19A23.99 23.99 0 000 24c0 3.77.9 7.35 2.56 10.56l7.97-5.97z"/><path fill="#EA4335" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 5.97C6.51 42.62 14.62 48 24 48z"/></svg>
          <span style={{ fontSize: 13, fontWeight: 500, color: "#5f6368" }}>Shopping</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", background: "#f8f9fa", border: "1px solid #dadce0", borderRadius: 24, padding: "6px 14px", marginBottom: 8, gap: 8 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9aa0a6" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
          <span style={{ fontSize: 13, color: "#202124", flex: 1 }}>Luxury Laptop Backpack</span>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#9aa0a6" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </div>
        <div style={{ display: "flex", gap: 20 }}>
          {["All","Shopping","Images","News"].map((t,i) => (
            <div key={t} style={{ fontSize: 11, paddingBottom: 7, fontWeight: i === 1 ? 600 : 400, color: i === 1 ? "#4B6AE8" : "#5f6368", borderBottom: i === 1 ? "2.5px solid #4B6AE8" : "2.5px solid transparent" }}>{t}</div>
          ))}
        </div>
      </div>
      <div style={{ padding: "8px 12px 6px", display: "flex", alignItems: "center", gap: 6, height: 36 }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: "#202124" }}>Sponsored products</span>
        {isRanked && (
          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6, background: "linear-gradient(135deg, #ecfdf5, #d1fae5)", border: "1px solid #6ee7b7", borderRadius: 20, padding: "4px 12px", animation: "popIn 0.35s cubic-bezier(0.34,1.56,0.64,1)", boxShadow: "0 2px 8px rgba(16,185,129,0.2)" }}>
            <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#10b981", animation: "pulseMF 1.8s infinite" }}/>
            <span style={{ fontSize: 11, fontWeight: 700, color: "#059669" }}>Your product is now #1 🎉</span>
          </div>
        )}
      </div>
      <div style={{ padding: "6px 14px 12px", overflow: "hidden" }}>
        <div style={{ display: "flex", gap: 10 }}>
          <div style={{ flex: "1 1 0", minWidth: 0, transform: isRanked ? "translateX(calc(100% + 10px))" : "translateX(0)", transition: "transform 0.75s cubic-bezier(0.34,1.2,0.64,1)" }}>
            <ProductCard isOur={false} dim={isRanked}/>
          </div>
          <div style={{ flex: "1 1 0", minWidth: 0, transform: isRanked ? "translateX(calc(-100% - 10px))" : "translateX(0)", transition: "transform 0.75s cubic-bezier(0.34,1.2,0.64,1)" }}>
            <ProductCard isOur={true} title={title} price="$89.99" optimized={isOptimized} ranked={isRanked} scanX={scanX} scanning={isScanning} dim={false}/>
          </div>
          <ProductCard isOur={false} dim={false}/>
          <div className="mf-hide-mobile" style={{ flex: "1 1 0", minWidth: 0 }}><ProductCard isOur={false} dim={false}/></div>
        </div>
      </div>
    </div>
  );
}

/* ── AI Fix Card (looping) ── */
const FIX_ISSUES = [
  { text: "Titles too short on 2/3 products", severity: "Critical" },
  { text: "Brand name missing in all titles", severity: "Critical" },
  { text: "Product Highlights not configured", severity: "Critical" },
  { text: "Descriptions under 500 chars", severity: "Warning" },
  { text: "No material or color attributes", severity: "Warning" },
];
const FIX_SCORES = [32, 45, 61, 78, 94, 98];

function getScoreColor(s) {
  if (s < 40) return "#EF4444";
  if (s < 70) return "#F59E0B";
  return "#10B981";
}

function AIFixCard() {
  const [fixedRows, setFixedRows] = useState(0);
  const [score, setScore] = useState(32);
  const [optimizedLabel, setOptimizedLabel] = useState(false);
  const running = useRef(true);

  useEffect(() => {
    running.current = true;
    let timeout;
    const wait = (ms) => new Promise(r => { timeout = setTimeout(r, ms); });

    const countTo = (from, to, duration) => {
      return new Promise(resolve => {
        const start = Date.now();
        const tick = () => {
          if (!running.current) { resolve(); return; }
          const p = Math.min((Date.now() - start) / duration, 1);
          setScore(Math.round(from + (to - from) * p));
          if (p < 1) requestAnimationFrame(tick); else resolve();
        };
        requestAnimationFrame(tick);
      });
    };

    const runLoop = async () => {
      while (running.current) {
        // Reset
        setFixedRows(0);
        setScore(32);
        setOptimizedLabel(false);
        await wait(1000);
        if (!running.current) break;

        // Fix rows one by one
        for (let i = 0; i < 5; i++) {
          setFixedRows(i + 1);
          await countTo(FIX_SCORES[i], FIX_SCORES[i + 1], 300);
          if (!running.current) break;
          await wait(500);
          if (!running.current) break;
        }
        if (!running.current) break;

        // Optimized
        setOptimizedLabel(true);
        await wait(4000);
        if (!running.current) break;
      }
    };

    runLoop();
    return () => { running.current = false; clearTimeout(timeout); };
  }, []);

  const scoreColor = getScoreColor(score);

  return (
    <div style={{
      background: "#fff", borderRadius: 8, border: "1px solid #e1e3e5",
      boxShadow: "0 1px 2px rgba(0,0,0,0.05)", overflow: "hidden",
    }}>
      <div className="mf-fix-layout" style={{ display: "flex", minHeight: 220 }}>
        {/* Left column — issues */}
        <div className="mf-fix-issues" style={{ flex: 1, padding: "18px 20px" }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: fixedRows >= 5 ? "#10B981" : "#EF4444", marginBottom: 12, transition: "color 0.3s ease" }}>
            {fixedRows >= 5 ? "✓ All issues fixed" : "Issues detected"}
          </div>
          {FIX_ISSUES.map((issue, i) => {
            const fixed = i < fixedRows;
            return (
              <div key={i} style={{
                display: "flex", alignItems: "center", gap: 8, padding: "6px 8px", marginBottom: 4,
                borderRadius: 8, background: fixed ? "#F0FDF4" : "transparent",
                transition: "background 0.3s ease",
              }}>
                <span style={{ fontSize: 13, fontWeight: 700, flexShrink: 0, color: fixed ? "#10B981" : "#EF4444", transition: "color 0.3s ease" }}>
                  {fixed ? "✓" : "✗"}
                </span>
                <span style={{ fontSize: 12, flex: 1, color: fixed ? "#059669" : "#6b7280", transition: "color 0.3s ease" }}>
                  {issue.text}
                </span>
                {!fixed && (
                  <span style={{
                    fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 20, flexShrink: 0,
                    color: issue.severity === "Critical" ? "#EF4444" : "#F59E0B",
                    background: issue.severity === "Critical" ? "#FEE2E2" : "#FEF3C7",
                  }}>
                    {issue.severity}
                  </span>
                )}
              </div>
            );
          })}
        </div>

        {/* Divider */}
        <div className="mf-fix-divider" style={{ width: 1, background: "#E5E7EB", flexShrink: 0 }}/>

        {/* Right column — score */}
        <div className="mf-fix-score" style={{ width: 180, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 20, position: "relative" }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 2 }}>
            <span style={{ fontSize: 52, fontWeight: 800, lineHeight: 1, letterSpacing: -2, color: scoreColor, transition: "color 0.3s ease" }}>
              {score}
            </span>
            <span style={{ fontSize: 18, fontWeight: 500, color: "#94a3b8" }}>/100</span>
          </div>
          <div style={{
            fontSize: 12, marginTop: 6, fontWeight: optimizedLabel ? 700 : 400,
            color: optimizedLabel ? "#10B981" : "#94a3b8",
            transition: "color 0.3s ease",
            animation: optimizedLabel ? "pulseMF 1.5s ease-in-out infinite" : "none",
            whiteSpace: "nowrap", textAlign: "center",
          }}>
            {optimizedLabel ? "✦ Optimized!" : "Feed Quality Score"}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════
   FINAL PAGE — /final
   Same template as homepage
   ══════════════════════════════════════════════ */
function ImpactCascade() {
  const [step, setStep] = useState(0); // 0=hidden, 1/2/3 = each step visible
  const [counters, setCounters] = useState([0, 0, 0]);
  const running = useRef(true);

  useEffect(() => {
    running.current = true;
    let timeout;
    const wait = (ms) => new Promise(r => { timeout = setTimeout(r, ms); });

    const countUp = (idx, target, duration) => {
      return new Promise(resolve => {
        const start = Date.now();
        const tick = () => {
          if (!running.current) { resolve(); return; }
          const p = Math.min((Date.now() - start) / duration, 1);
          setCounters(prev => { const n = [...prev]; n[idx] = Math.round(p * target); return n; });
          if (p < 1) requestAnimationFrame(tick); else resolve();
        };
        requestAnimationFrame(tick);
      });
    };

    const runLoop = async () => {
      while (running.current) {
        setCounters([0, 0, 0]); setStep(0);
        await wait(800);
        if (!running.current) break;

        // Step 1 — Impressions
        setStep(1);
        await countUp(0, 67, 800);
        if (!running.current) break;
        await wait(600);

        // Step 2 — Clicks
        setStep(2);
        await countUp(1, 43, 800);
        if (!running.current) break;
        await wait(600);

        // Step 3 — Revenue
        setStep(3);
        await countUp(2, 38, 800);
        if (!running.current) break;
        await wait(4000);
        if (!running.current) break;
      }
    };
    runLoop();
    return () => { running.current = false; clearTimeout(timeout); };
  }, []);

  const steps = [
    { icon: "👁", value: counters[0], label: "More Impressions", sub: "Your products appear more often in Shopping results", color: "#4361EE" },
    { icon: "👆", value: counters[1], label: "More Clicks", sub: "Better titles & descriptions = higher click-through rate", color: "#4361EE" },
    { icon: "💰", value: counters[2], label: "More Revenue", sub: "Same ad budget, more sales — pure profit increase", color: "#10B981" },
  ];

  return (
    <div className="mf-cascade" style={{
      background: "#fff", borderRadius: 14,
      border: "1px solid rgba(75,106,232,0.1)",
      boxShadow: "0 4px 20px rgba(75,106,232,0.07)",
      padding: "28px 28px",
    }}>
      {steps.map((s, i) => (
        <div key={i}>
          {/* Arrow between steps */}
          {i > 0 && (
            <div style={{ textAlign: "center", margin: "8px 0", opacity: step > i ? 1 : 0, transition: "opacity 0.3s ease" }}>
              <span style={{ fontSize: 16, color: "#C7D2FE" }}>↓</span>
            </div>
          )}
          {/* Step row */}
          <div style={{
            display: "flex", alignItems: "center", gap: 14, padding: "10px 14px",
            borderRadius: 10,
            background: step >= i + 1 && i === 2 ? "#F0FDF4" : "transparent",
            opacity: step >= i + 1 ? 1 : 0.3,
            transform: step >= i + 1 ? "translateX(0)" : "translateX(-8px)",
            transition: "all 0.4s ease",
          }}>
            <span style={{ fontSize: 20, flexShrink: 0 }}>{s.icon}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="mf-cascade-step-label" style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>{s.label}</div>
              <div className="mf-cascade-step-sub" style={{ fontSize: 11, color: "#9CA3AF" }}>{s.sub}</div>
            </div>
            <div className="mf-cascade-number" style={{ fontSize: 28, fontWeight: 800, color: s.color, letterSpacing: -1, flexShrink: 0 }}>
              +{s.value}%
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ── Mini feature animations ── */
function TitleRewriteAnim({ started, onDone }) {
  const BEFORE = "Laptop bag";
  const AFTER = "Premium Luxury Laptop Backpack 15.6\"";
  const [text, setText] = useState(BEFORE);
  const [phase, setPhase] = useState("before");
  const running = useRef(true);
  const hasStarted = useRef(false);

  useEffect(() => {
    if (!started || hasStarted.current) return;
    hasStarted.current = true;
    running.current = true;
    let timeout;
    const wait = (ms) => new Promise(r => { timeout = setTimeout(r, ms); });
    const runOnce = async () => {
      await wait(800);
      if (!running.current) return;
      setPhase("erasing");
      for (let i = BEFORE.length; i >= 0; i--) {
        setText(BEFORE.slice(0, i)); await wait(40); if (!running.current) return;
      }
      setPhase("typing");
      for (let i = 1; i <= AFTER.length; i++) {
        setText(AFTER.slice(0, i)); await wait(25); if (!running.current) return;
      }
      setPhase("done");
      if (onDone) onDone();
    };
    runOnce();
    return () => { running.current = false; clearTimeout(timeout); hasStarted.current = false; };
  }, [started]);

  return (
    <div style={{ background: "#f8f9fa", borderRadius: 8, overflow: "hidden", textAlign: "left" }}>
      <div style={{ width: "100%", height: 80, background: "#fff", display: "flex", alignItems: "center", justifyContent: "center", borderBottom: "1px solid #f1f1f1" }}>
        <img src="/backpack.png" alt="" style={{ height: "90%", objectFit: "contain" }}/>
      </div>
      <div style={{ padding: "8px 12px" }}>
        <div style={{ fontSize: 9, color: "#94a3b8", marginBottom: 3 }}>Product title</div>
        <div style={{ fontSize: 11, fontWeight: 600, color: phase === "done" ? "#10B981" : "#0f172a", lineHeight: 1.4, transition: "color 0.3s ease" }}>
          {text}<span style={{ opacity: phase === "erasing" || phase === "typing" ? 1 : 0, color: "#4361EE", animation: "pulseMF 0.8s infinite" }}>|</span>
        </div>
        {phase === "done" && (
          <div style={{ textAlign: "center", marginTop: 8 }}>
            <span style={{ fontSize: 9, fontWeight: 600, color: "#4361EE", background: "rgba(67,97,238,0.08)", border: "1px solid rgba(67,97,238,0.18)", padding: "3px 10px", borderRadius: 20, animation: "popIn 0.3s ease" }}>✦ SEO optimized</span>
          </div>
        )}
      </div>
    </div>
  );
}

function DescriptionGrowAnim({ started, onDone }) {
  const [chars, setChars] = useState(120);
  const [phase, setPhase] = useState("before");
  const running = useRef(true);
  const hasStarted = useRef(false);

  useEffect(() => {
    if (!started || hasStarted.current) return;
    hasStarted.current = true;
    running.current = true;
    let timeout;
    const wait = (ms) => new Promise(r => { timeout = setTimeout(r, ms); });
    const runOnce = async () => {
      setChars(120); setPhase("before");
      await wait(800); if (!running.current) return;
      setPhase("growing");
      await new Promise(resolve => {
        const start = Date.now();
        const tick = () => {
          if (!running.current) { resolve(); return; }
          const p = Math.min((Date.now() - start) / 1200, 1);
          setChars(Math.round(120 + p * (4850)));
          if (p < 1) requestAnimationFrame(tick); else resolve();
        };
        requestAnimationFrame(tick);
      });
      if (!running.current) return;
      setPhase("done");
      if (onDone) onDone();
    };
    runOnce();
    return () => { running.current = false; clearTimeout(timeout); hasStarted.current = false; };
  }, [started]);

  const pct = Math.min(chars / 5000 * 100, 100);
  return (
    <div style={{ background: "#f8f9fa", borderRadius: 8, overflow: "hidden", textAlign: "left" }}>
      <div style={{ width: "100%", height: 80, background: "#fff", display: "flex", alignItems: "center", justifyContent: "center", borderBottom: "1px solid #f1f1f1" }}>
        <img src="/products/shoes.webp" alt="" style={{ height: "90%", objectFit: "contain" }}/>
      </div>
      <div style={{ padding: "8px 12px" }}>
        <div style={{ fontSize: 9, color: "#94a3b8", marginBottom: 6 }}>Description length</div>
        <div style={{ height: 6, borderRadius: 999, background: "#E5E7EB", overflow: "hidden", marginBottom: 6 }}>
          <div style={{ height: "100%", borderRadius: 999, background: phase === "done" ? "#10B981" : "#4361EE", width: `${pct}%`, transition: "background 0.3s ease" }}/>
        </div>
        <div style={{ fontSize: 11, fontWeight: 700, color: phase === "done" ? "#10B981" : "#0f172a", textAlign: "right", transition: "color 0.3s ease" }}>
          {chars.toLocaleString()} / 5,000 chars
        </div>
        {phase === "done" && (
          <div style={{ textAlign: "center", marginTop: 8 }}>
            <span style={{ fontSize: 9, fontWeight: 600, color: "#4361EE", background: "rgba(67,97,238,0.08)", border: "1px solid rgba(67,97,238,0.18)", padding: "3px 10px", borderRadius: 20, animation: "popIn 0.3s ease" }}>✦ SEO optimized</span>
          </div>
        )}
      </div>
    </div>
  );
}

function HighlightsAnim({ started, onDone }) {
  const items = ["Material", "Fit & sizing", "Care instructions", "Season", "Certification"];
  const [visible, setVisible] = useState(0);
  const running = useRef(true);
  const hasStarted = useRef(false);

  useEffect(() => {
    if (!started || hasStarted.current) return;
    hasStarted.current = true;
    running.current = true;
    let timeout;
    const wait = (ms) => new Promise(r => { timeout = setTimeout(r, ms); });
    const runOnce = async () => {
      setVisible(0);
      await wait(800); if (!running.current) return;
      for (let i = 1; i <= 5; i++) {
        setVisible(i); await wait(500); if (!running.current) return;
      }
      if (onDone) onDone();
    };
    runOnce();
    return () => { running.current = false; clearTimeout(timeout); hasStarted.current = false; };
  }, [started]);

  return (
    <div style={{ background: "#f8f9fa", borderRadius: 8, overflow: "hidden", textAlign: "left" }}>
      <div style={{ width: "100%", height: 80, background: "#fff", display: "flex", alignItems: "center", justifyContent: "center", borderBottom: "1px solid #f1f1f1" }}>
        <img src="/products/tshirt.avif" alt="" style={{ height: "90%", objectFit: "contain" }}/>
      </div>
      <div style={{ padding: "8px 12px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <span style={{ fontSize: 9, color: "#94a3b8" }}>Highlights</span>
          <span style={{ fontSize: 10, fontWeight: 700, color: visible >= 5 ? "#10B981" : "#0f172a", transition: "color 0.3s ease" }}>{Math.min(visible, 5)}/5</span>
        </div>
        {items.map((item, i) => (
          <div key={i} style={{
            fontSize: 11, color: i < visible ? "#374151" : "#d1d5db", lineHeight: 1.2, padding: "3px 0",
            display: "flex", alignItems: "center", gap: 6,
            transition: "color 0.2s ease",
          }}>
            <span style={{ color: i < visible ? "#10B981" : "#d1d5db", fontSize: 11, fontWeight: 700, transition: "color 0.2s ease" }}>
              {i < visible ? "✓" : "○"}
            </span>
            {item}
          </div>
        ))}
      </div>
    </div>
  );
}

function IntroducingLogo() {
  const [phase, setPhase] = useState(0); // 0=hidden, 1=text fading in, 2=logo pop
  const running = useRef(true);

  useEffect(() => {
    running.current = true;
    let timeout;
    const wait = (ms) => new Promise(r => { timeout = setTimeout(r, ms); });
    const run = async () => {
      while (running.current) {
        setPhase(0);
        await wait(600); if (!running.current) break;
        setPhase(1); // "Introducing" fades in
        await wait(1200); if (!running.current) break;
        setPhase(2); // Logo pops in
        await wait(6000); if (!running.current) break;
      }
    };
    run();
    return () => { running.current = false; clearTimeout(timeout); };
  }, []);

  return (
    <div style={{ textAlign: "center", marginBottom: 20 }}>
      {/* "Introducing" text */}
      <div style={{
        fontSize: 13, fontWeight: 600, letterSpacing: 2, textTransform: "uppercase",
        color: "#4361EE", marginBottom: 14,
        opacity: phase >= 1 ? 1 : 0,
        transform: phase >= 1 ? "translateY(0)" : "translateY(8px)",
        transition: "opacity 0.6s ease, transform 0.6s ease",
      }}>
        Introducing
      </div>
      {/* Logo with sparkles */}
      <div style={{ position: "relative", display: "inline-block" }}>
        {/* Orbiting sparkles — only after logo appears */}
        {phase >= 2 && [
          { anim: "sparkleOrbit1", size: 12, opacity: 0.6 },
          { anim: "sparkleOrbit2", size: 9, opacity: 0.45 },
          { anim: "sparkleOrbit3", size: 10, opacity: 0.55 },
        ].map((s, i) => (
          <svg key={i} width={s.size} height={s.size} viewBox="0 0 16 16" fill="none" style={{
            position: "absolute", top: "50%", left: "50%", marginTop: -s.size/2, marginLeft: -s.size/2,
            animation: `${s.anim} ${5 + i}s linear infinite`, zIndex: 2, pointerEvents: "none",
          }}>
            <path d="M8 0L9.4 6.6L16 8L9.4 9.4L8 16L6.6 9.4L0 8L6.6 6.6Z" fill="#4B6AE8" opacity={s.opacity}/>
          </svg>
        ))}
        {/* Logo */}
        <img src="/logo.png" alt="MagicFeedPro" style={{
          height: 52, position: "relative", zIndex: 1,
          opacity: phase >= 2 ? 1 : 0,
          transform: phase >= 2 ? "scale(1)" : "scale(0.7)",
          transition: "opacity 0.4s cubic-bezier(0.34,1.56,0.64,1), transform 0.4s cubic-bezier(0.34,1.56,0.64,1)",
          filter: phase >= 2 ? "drop-shadow(0 0 12px rgba(75,106,232,0.25))" : "none",
        }}/>
      </div>
    </div>
  );
}

function ExitPopupAnim() {
  const [score, setScore] = useState(32);
  const [phase, setPhase] = useState("bad"); // bad → fixing → good
  const running = useRef(true);

  useEffect(() => {
    running.current = true;
    let timeout;
    const wait = (ms) => new Promise(r => { timeout = setTimeout(r, ms); });
    const countTo = (from, to, duration) => new Promise(resolve => {
      const start = Date.now();
      const tick = () => {
        if (!running.current) { resolve(); return; }
        const p = Math.min((Date.now() - start) / duration, 1);
        setScore(Math.round(from + (to - from) * p));
        if (p < 1) requestAnimationFrame(tick); else resolve();
      };
      requestAnimationFrame(tick);
    });

    const run = async () => {
      while (running.current) {
        setScore(32); setPhase("bad");
        await wait(1500); if (!running.current) break;
        setPhase("fixing");
        await countTo(32, 98, 1800);
        if (!running.current) break;
        setPhase("good");
        await wait(3500); if (!running.current) break;
      }
    };
    run();
    return () => { running.current = false; clearTimeout(timeout); };
  }, []);

  const color = score < 40 ? "#EF4444" : score < 70 ? "#F59E0B" : "#10B981";
  const pct = score / 100;

  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 24, margin: "20px 0 8px" }}>
      {/* Circular score gauge */}
      <div style={{ position: "relative", width: 80, height: 80, flexShrink: 0 }}>
        <svg width="80" height="80" viewBox="0 0 80 80" style={{ transform: "rotate(-90deg)" }}>
          <circle cx="40" cy="40" r="34" fill="none" stroke="#E5E7EB" strokeWidth="6"/>
          <circle cx="40" cy="40" r="34" fill="none" stroke={color} strokeWidth="6"
            strokeDasharray={`${pct * 213.6} 213.6`}
            strokeLinecap="round"
            style={{ transition: "stroke 0.3s ease" }}/>
        </svg>
        <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column" }}>
          <span style={{ fontSize: 22, fontWeight: 800, color, transition: "color 0.3s ease", lineHeight: 1 }}>{score}</span>
          <span style={{ fontSize: 8, color: "#94a3b8" }}>/100</span>
        </div>
      </div>
      {/* Status text */}
      <div style={{ textAlign: "left" }}>
        {phase === "bad" && (
          <div style={{ animation: "popIn 0.3s ease" }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#EF4444", marginBottom: 4 }}>✗ Feed not optimized</div>
            <div style={{ fontSize: 10, color: "#9CA3AF", lineHeight: 1.4 }}>Titles too short<br/>Missing highlights<br/>Low description quality</div>
          </div>
        )}
        {phase === "fixing" && (
          <div style={{ animation: "popIn 0.3s ease" }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#F59E0B", marginBottom: 4 }}>⟳ AI is optimizing...</div>
            <div style={{ fontSize: 10, color: "#9CA3AF", lineHeight: 1.4 }}>Rewriting titles<br/>Expanding descriptions<br/>Adding highlights</div>
          </div>
        )}
        {phase === "good" && (
          <div style={{ animation: "popIn 0.3s ease" }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#10B981", marginBottom: 4 }}>✓ Feed fully optimized</div>
            <div style={{ fontSize: 10, color: "#9CA3AF", lineHeight: 1.4 }}>+67% impressions<br/>+43% clicks<br/>+38% revenue</div>
          </div>
        )}
      </div>
    </div>
  );
}

function ExitPopup() {
  const [show, setShow] = useState(false);
  const triggered = useRef(false);

  useEffect(() => {
    const handleMouseLeave = (e) => {
      if (e.clientY <= 0 && !triggered.current) {
        triggered.current = true;
        setShow(true);
      }
    };
    document.addEventListener("mouseleave", handleMouseLeave);
    return () => document.removeEventListener("mouseleave", handleMouseLeave);
  }, []);

  if (!show) return null;

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 9999,
      background: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)",
      display: "flex", alignItems: "center", justifyContent: "center",
      animation: "popIn 0.3s ease",
    }} onClick={() => setShow(false)}>
      <div style={{
        background: "#fff", borderRadius: 20, padding: "36px 32px",
        maxWidth: 400, width: "90%", textAlign: "center",
        boxShadow: "0 24px 80px rgba(75,106,232,0.25)",
        position: "relative",
      }} onClick={e => e.stopPropagation()}>
        {/* Close button */}
        <button onClick={() => setShow(false)} style={{
          position: "absolute", top: 14, right: 14,
          background: "none", border: "none", cursor: "pointer",
          fontSize: 18, color: "#94a3b8", lineHeight: 1,
        }}>✕</button>

        <h3 style={{ fontSize: 21, fontWeight: 800, color: "#0D0D1A", lineHeight: 1.3 }}>
          Your competitors are already<br/>
          <span style={{ background: "linear-gradient(135deg,#4B6AE8,#818cf8)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>outranking you</span>
        </h3>

        <ExitPopupAnim/>

        <p style={{ fontSize: 13, color: "#6B7280", lineHeight: 1.5, margin: "12px 0 22px" }}>
          Every day without optimization = lost sales.<br/>
          <span style={{ fontWeight: 700, color: "#0D0D1A" }}>Fix it in 10 minutes.</span>
        </p>
        <a href="https://app.magicfeedpro.com/register" onClick={() => { try { trackCtaFinal(); } catch {} }} style={{
          display: "inline-block", padding: "14px 32px",
          background: "linear-gradient(135deg, #4B6AE8, #6B8BF5)",
          border: "none", borderRadius: 12,
          color: "#fff", fontSize: 15, fontWeight: 700,
          fontFamily: "'Inter',sans-serif",
          textDecoration: "none",
          boxShadow: "0 4px 16px rgba(75,106,232,0.35)",
          transition: "transform 0.2s ease, box-shadow 0.2s ease",
        }}
          onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 8px 24px rgba(75,106,232,0.45)"; }}
          onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 4px 16px rgba(75,106,232,0.35)"; }}
        >
          → Optimize My Feed Now
        </a>
        <p style={{ fontSize: 12, color: "#94a3b8", marginTop: 14 }}>
          Free to start • No credit card required
        </p>
      </div>
    </div>
  );
}

export { ShoppingWidget, AIFixCard, ImpactCascade, TitleRewriteAnim, DescriptionGrowAnim, HighlightsAnim, IntroducingLogo, ExitPopupAnim };

export default function FinalPage() {
  const [animStep, setAnimStep] = useState(1); // 1=title running, 2=description, 3=highlights
  const [animCycle, setAnimCycle] = useState(0);

  useEffect(() => { initTracking(); trackPageView("final"); }, []);
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        html, body, #root { min-height:100%; }
        @keyframes floatMF { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-6px)} }
        @keyframes pulseMF { 0%,100%{opacity:1} 50%{opacity:0.3} }
        @keyframes shimmerMF { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
        @keyframes popIn { 0%{opacity:0;transform:scale(0.75)} 100%{opacity:1;transform:scale(1)} }
        @keyframes revenuePop { 0%{transform:scale(1)} 50%{transform:scale(1.05)} 100%{transform:scale(1)} }
        @keyframes confettiBurst { 0%{opacity:1;transform:translateY(0) scale(1)} 100%{opacity:0;transform:translateY(-60px) scale(0.5)} }
        @keyframes logoShimmer { 0%{left:-100%} 100%{left:200%} }
        @keyframes sparkleOrbit1 { 0%{transform:rotate(0deg) translateX(52px) rotate(0deg) scale(0.8)} 50%{transform:rotate(180deg) translateX(52px) rotate(-180deg) scale(1.2)} 100%{transform:rotate(360deg) translateX(52px) rotate(-360deg) scale(0.8)} }
        @keyframes sparkleOrbit2 { 0%{transform:rotate(120deg) translateX(60px) rotate(-120deg) scale(1)} 50%{transform:rotate(300deg) translateX(60px) rotate(-300deg) scale(0.7)} 100%{transform:rotate(480deg) translateX(60px) rotate(-480deg) scale(1)} }
        @keyframes sparkleOrbit3 { 0%{transform:rotate(240deg) translateX(48px) rotate(-240deg) scale(0.9)} 50%{transform:rotate(420deg) translateX(48px) rotate(-420deg) scale(1.3)} 100%{transform:rotate(600deg) translateX(48px) rotate(-600deg) scale(0.9)} }
        @keyframes glowPulse { 0%,100%{box-shadow:0 0 20px rgba(75,106,232,0.15)} 50%{box-shadow:0 0 40px rgba(75,106,232,0.3)} }
        @keyframes shimmerText { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
        .mf-hide-mobile { display:block; }

        /* Tablet */
        @media (max-width: 768px) {
          .mf-main-card-f { padding:28px 20px !important; }
          .mf-headline-f { font-size:20px !important; }
          .mf-fix-layout { flex-direction:column !important; }
          .mf-fix-layout .mf-fix-divider { width:100% !important; height:1px !important; }
          .mf-fix-layout .mf-fix-score { width:100% !important; padding:16px 20px !important; flex-direction:column !important; justify-content:center !important; align-items:center !important; gap:4px !important; position:static !important; overflow:hidden !important; }
          .mf-fix-layout { min-height:auto !important; }
          .mf-cascade { padding:20px 12px !important; }
          .mf-cta-card-f { padding:32px 20px !important; }
          .mf-cascade-number { font-size:26px !important; }
          .mf-features-row { flex-direction:column !important; gap:12px !important; }
          .mf-features-row > div { min-width:0 !important; }
          .mf-intro-subtitle { font-size:18px !important; }
          .mf-features-label { font-size:14px !important; }
        }

        /* Mobile */
        @media (max-width: 480px) {
          .mf-hide-mobile { display:none !important; }
          .mf-product-card-f { height: 260px !important; }
          .mf-product-img-f { height: 75px !important; }
          .mf-ai-badge-f { height: 16px !important; margin-bottom: 3px !important; }
          .mf-ai-badge-f span { font-size: 7px !important; padding: 1px 5px !important; }
          .mf-main-card-f { padding:18px 14px !important; }
          .mf-outer-card-f { border-radius:20px !important; }
          .mf-headline-f { font-size:17px !important; line-height:1.35 !important; }
          .mf-transition-f { font-size:13px !important; line-height:1.5 !important; }
          .mf-fix-layout .mf-fix-issues { padding:12px 10px !important; }
          .mf-fix-layout .mf-fix-issues > div { font-size:11px !important; }
          .mf-fix-layout .mf-fix-score { padding:12px !important; flex-direction:column !important; gap:4px !important; }
          .mf-fix-layout .mf-fix-score span:first-child { font-size:36px !important; }
          .mf-cascade { padding:16px 8px !important; }
          .mf-cascade-row { gap:0 !important; }
          .mf-cascade-number { font-size:20px !important; }
          .mf-cascade-step-label { font-size:12px !important; }
          .mf-cascade-step-sub { font-size:10px !important; }
          .mf-cta-card-f { padding:28px 16px !important; }
          .mf-cta-card-f h2 { font-size:18px !important; }
          .mf-impact-title { font-size:16px !important; }
          .mf-intro-subtitle { font-size:16px !important; }
          .mf-features-label { font-size:13px !important; }
          .mf-features-row { gap:10px !important; }
          .mf-features-row > div { padding:14px !important; }
          .mf-feature-title { font-size:13px !important; }
          .btn-primary-final { padding:12px 28px !important; font-size:14px !important; }
        }
      `}</style>

      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Inter',sans-serif", background: "linear-gradient(135deg,#f0f4ff 0%,#e8eeff 50%,#dde5ff 100%)", padding: "20px", position: "relative", overflow: "hidden" }}>

        {/* Background decorative blurs */}
        <div style={{ position:"absolute", width:600, height:600, borderRadius:"50%", background:"radial-gradient(circle,rgba(75,106,232,0.08) 0%,transparent 70%)", top:"-10%", left:"-5%", pointerEvents:"none" }}/>
        <div style={{ position:"absolute", width:500, height:500, borderRadius:"50%", background:"radial-gradient(circle,rgba(139,92,246,0.06) 0%,transparent 70%)", bottom:"-15%", right:"-5%", pointerEvents:"none" }}/>
        <div style={{ position:"absolute", width:300, height:300, borderRadius:"50%", background:"radial-gradient(circle,rgba(75,106,232,0.05) 0%,transparent 70%)", top:"50%", left:"50%", transform:"translate(-50%,-50%)", pointerEvents:"none" }}/>

        {/* Floating sparkles on background */}
        {[[8,12,14,0],[85,8,10,1],[92,75,13,2],[5,80,11,1.5]].map(([x,y,s,d],i) => (
          <svg key={i} width={s} height={s} viewBox="0 0 16 16" fill="none" style={{ position:"absolute", left:`${x}%`, top:`${y}%`, animation:`floatMF 4s ease-in-out ${d}s infinite`, zIndex:0 }}>
            <path d="M8 0L9.4 6.6L16 8L9.4 9.4L8 16L6.6 9.4L0 8L6.6 6.6Z" fill="#a5b4fc" opacity="0.45"/>
          </svg>
        ))}

        {/* Single centered column */}
        <div style={{ maxWidth: 672, width: "100%", zIndex: 1 }}>

          {/* ── MAIN CARD ── */}
          <div className="mf-outer-card-f" style={{ background: "linear-gradient(180deg, #f8faff 0%, #f0f4ff 100%)", borderRadius: 28, overflow: "hidden", boxShadow: "0 12px 60px rgba(75,106,232,0.12), 0 2px 8px rgba(0,0,0,0.04)", position: "relative" }}>

            <div className="mf-main-card-f" style={{ padding: "32px 36px", position: "relative" }}>

              {/* Floating sparkles inside card */}
              {[[62,8,14,0],[70,14,10,1],[15,72,13,2]].map(([x,y,s,d],i) => (
                <svg key={i} width={s} height={s} viewBox="0 0 16 16" fill="none" style={{ position:"absolute", left:`${x}%`, top:`${y}%`, animation:`floatMF 4s ease-in-out ${d}s infinite` }}>
                  <path d="M8 0L9.4 6.6L16 8L9.4 9.4L8 16L6.6 9.4L0 8L6.6 6.6Z" fill="#a5b4fc" opacity="0.35"/>
                </svg>
              ))}

              {/* SECTION 1 — Transition text */}
              <p className="mf-headline-f" style={{ fontSize: 26, fontWeight: 800, color: "#0D0D1A", textAlign: "center", margin: "0 auto 32px", maxWidth: 560, lineHeight: 1.45, letterSpacing: "-0.3px" }}>
                Optimize your feed{" "}
                <span style={{ background: "linear-gradient(135deg,#4B6AE8,#818cf8)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>with AI</span>
                <br/>
                <span style={{ fontWeight: 500, color: "#6B7280", fontSize: "0.82em" }}>and boost your{" "}
                  <span style={{ fontWeight: 700, color: "#0D0D1A" }}>Shopping performance</span>.
                </span>
              </p>

              {/* SECTION 2 — AI Fix Card */}
              <AIFixCard/>

              {/* SECTION 3 — Transition text */}
              <p className="mf-transition-f" style={{ fontSize: 17, fontWeight: 600, color: "#0f172a", textAlign: "center", margin: "36px auto", maxWidth: 520, lineHeight: 1.6 }}>
                Improve your product attributes{" "}
                <span style={{ background: "linear-gradient(135deg,#4B6AE8,#818cf8)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", fontWeight: 700 }}>with AI</span>
                {" "}and climb<br/>
                <span style={{ color: "#4285F4" }}>G</span>
                <span style={{ color: "#EA4335" }}>o</span>
                <span style={{ color: "#FBBC05" }}>o</span>
                <span style={{ color: "#4285F4" }}>g</span>
                <span style={{ color: "#34A853" }}>l</span>
                <span style={{ color: "#EA4335" }}>e</span>
                {" "}Shopping rankings to drive more sales.
              </p>

              {/* SECTION 4 — Google Shopping Animation */}
              <ShoppingWidget/>

              {/* Impact cascade section */}
              <p className="mf-impact-title" style={{ fontSize: 20, fontWeight: 700, color: "#0D0D1A", textAlign: "center", margin: "48px auto 32px" }}>
                Here's the real impact of an optimized feed.
              </p>
              <ImpactCascade/>

              {/* Features section */}
              <div style={{ margin: "48px 0", textAlign: "center" }}>
                <IntroducingLogo/>

                <h2 className="mf-intro-subtitle" style={{ fontSize: 22, fontWeight: 500, color: "#6B7280", lineHeight: 1.4, marginBottom: 16 }}>
                  The only <span style={{ fontWeight: 800, color: "#0D0D1A" }}>AI tool</span> that maximizes your{" "}
                  <span style={{ fontWeight: 800, background: "linear-gradient(135deg,#4B6AE8,#818cf8)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>Shopping sales</span>{" "}
                  — automatically.
                </h2>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12, marginBottom: 36, flexWrap: "wrap" }}>
                  <div className="mf-hide-mobile" style={{ width: 40, height: 2, borderRadius: 2, background: "linear-gradient(90deg, transparent, #4361EE)" }}/>
                  <span className="mf-features-label" style={{ fontSize: 16, fontWeight: 700, color: "#0D0D1A", textAlign: "center" }}>
                    Here's what MagicFeedPro optimizes <span style={{ color: "#4361EE" }}>for you</span>
                  </span>
                  <div className="mf-hide-mobile" style={{ width: 40, height: 2, borderRadius: 2, background: "linear-gradient(90deg, #4361EE, transparent)" }}/>
                </div>
                <div className="mf-features-row" style={{ display: "flex", gap: 16 }}>
                  {/* Card 1 — Titles */}
                  <div style={{ flex: "1 1 0", minWidth: 0, background: "#fff", borderRadius: 16, border: "1.5px solid #E5E7EB", padding: 20, transition: "border-color 0.15s ease, box-shadow 0.15s ease", cursor: "default" }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = "#4361EE"; e.currentTarget.style.boxShadow = "0 4px 16px rgba(67,97,238,0.1)"; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = "#E5E7EB"; e.currentTarget.style.boxShadow = "none"; }}
                  >
                    <div className="mf-feature-title" style={{ fontSize: 15, fontWeight: 700, color: "#0D0D1A", marginBottom: 12, textAlign: "center" }}>
                      <span style={{ color: "#4361EE" }}>✦</span> AI-Optimized Titles
                    </div>
                    <TitleRewriteAnim key={`t-${animCycle}`} started={animStep >= 1} onDone={() => setAnimStep(2)}/>
                  </div>
                  {/* Card 2 — Descriptions */}
                  <div style={{ flex: "1 1 0", minWidth: 0, background: "#fff", borderRadius: 16, border: "1.5px solid #E5E7EB", padding: 20, transition: "border-color 0.15s ease, box-shadow 0.15s ease", cursor: "default" }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = "#4361EE"; e.currentTarget.style.boxShadow = "0 4px 16px rgba(67,97,238,0.1)"; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = "#E5E7EB"; e.currentTarget.style.boxShadow = "none"; }}
                  >
                    <div className="mf-feature-title" style={{ fontSize: 15, fontWeight: 700, color: "#0D0D1A", marginBottom: 12, textAlign: "center" }}>
                      📄 Smarter Descriptions
                    </div>
                    <DescriptionGrowAnim key={`d-${animCycle}`} started={animStep >= 2} onDone={() => setAnimStep(3)}/>
                  </div>
                  {/* Card 3 — Highlights */}
                  <div style={{ flex: "1 1 0", minWidth: 0, background: "#fff", borderRadius: 16, border: "1.5px solid #E5E7EB", padding: 20, transition: "border-color 0.15s ease, box-shadow 0.15s ease", cursor: "default" }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = "#4361EE"; e.currentTarget.style.boxShadow = "0 4px 16px rgba(67,97,238,0.1)"; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = "#E5E7EB"; e.currentTarget.style.boxShadow = "none"; }}
                  >
                    <div className="mf-feature-title" style={{ fontSize: 15, fontWeight: 700, color: "#0D0D1A", marginBottom: 12, textAlign: "center" }}>
                      ⭐ Product Highlights
                    </div>
                    <HighlightsAnim key={`h-${animCycle}`} started={animStep >= 3} onDone={() => {
                      setTimeout(() => { setAnimStep(1); setAnimCycle(c => c + 1); }, 4000);
                    }}/>
                  </div>
                </div>
              </div>

              {/* CTA — no box */}
              <div style={{ textAlign: "center", marginTop: 36 }}>
                <a href="https://app.magicfeedpro.com/register" className="btn-primary-final" onClick={() => { try { trackCtaFinal(); } catch {} }} style={{
                  display: "inline-block",
                  padding: "14px 36px",
                  background: "linear-gradient(135deg, #4B6AE8, #6B8BF5)",
                  border: "none", borderRadius: 12,
                  color: "#fff", fontSize: 15, fontWeight: 700,
                  fontFamily: "'Inter',sans-serif",
                  cursor: "pointer",
                  boxShadow: "0 4px 16px rgba(75,106,232,0.35)",
                  transition: "transform 0.2s ease, box-shadow 0.2s ease",
                  textDecoration: "none",
                }}
                  onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 8px 24px rgba(75,106,232,0.45)"; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 4px 16px rgba(75,106,232,0.35)"; }}
                >
                  → Optimize My Feed Now
                </a>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 4, marginTop: 20 }}>
                  {[...Array(5)].map((_, i) => (
                    <span key={i} style={{ fontSize: 12, color: "#FBBF24" }}>★</span>
                  ))}
                  <span style={{ fontSize: 12, color: "#6b7280", marginLeft: 4 }}>200+ merchants</span>
                </div>
                <p style={{ fontSize: 12, color: "#94a3b8", marginTop: 8 }}>
                  No contract • Setup in 10 min
                </p>
              </div>

            </div>
          </div>

        </div>{/* end max-w-2xl */}
      </div>
      <ExitPopup/>
    </>
  );
}
